import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerQuoteFonts } from "@/lib/pdfFont";
import { NUM, fmtDate, fmtDateTime, unitLabel } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { logoAspect, loadCompanyLogo } from "@/lib/branding";
import { MODE_LABEL, PAY_LABEL, r2 } from "@/lib/pos";
import { receiptBelgeNo } from "@/lib/receipt";

// Gün Sonu (Z) Raporu — vektörel PDF, satış fişiyle aynı sade dil: özet · ödeme & kasa · personel · SEPET BAZLI ayrıntı (kalem, maliyet, kâr)
const NAVY = [11, 25, 44], INK = [15, 23, 42], ORANGE = [234, 88, 32], SLATE = [100, 116, 139], MUTED = [148, 163, 184];
const LINE = [226, 232, 240], WASH = [248, 250, 252], HEAD_BG = [241, 245, 249], WHITE = [255, 255, 255], GREEN = [5, 150, 105], RED = [220, 38, 38], AMBER = [180, 83, 9];
const money = (v) => `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} ₺`;
const timeOf = (iso) => fmtDateTime(iso).slice(-5);
export const zReportFilename = (z) => `Gün Sonu Raporu ${fmtDate(z.date)}.pdf`;
const roleShort = (r) => (r === "admin" ? "Yön." : r === "worker" ? "Depo" : "");

export const buildZReportPdf = async (z, opts = {}) => {
  const showProfit = opts.showProfit !== false;
  const showItems = opts.showItems !== false;
  const pdf = new jsPDF("p", "mm", "a4");
  const F = await registerQuoteFonts(pdf);
  const logo = await loadCompanyLogo();
  const AR = logoAspect();
  const pageW = pdf.internal.pageSize.getWidth(), pageH = pdf.internal.pageSize.getHeight();
  const M = 14, W = pageW - M * 2, FOOT = 16, HEAD1 = 42, HEAD2 = 22;
  const T = (font, style, size, color) => { pdf.setFont(font, style); pdf.setFontSize(size); pdf.setTextColor(...color); };
  const fill = (c) => pdf.setFillColor(...c);
  const stroke = (c, w = 0.3) => { pdf.setDrawColor(...c); pdf.setLineWidth(w); };
  const fit = (text, maxW) => { let raw = String(text ?? ""), out = raw; while (pdf.getTextWidth(out) > maxW && raw.length > 2) { raw = raw.slice(0, -1); out = raw.trimEnd() + "…"; } return out; };
  const t = z.totals || {}, methods = z.methods || {};
  const sales = [...(z.sales || [])].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  const dateLabel = fmtDate(z.date);
  const drawn = new Set();

  const drawLogo = (x, y, h) => {
    const lw = Math.min(h * AR, 60);
    if (logo) { try { pdf.addImage(logo, "PNG", x, y, lw, h); return; } catch { /* fallthrough */ } }
    T(F.head, "bold", h * 0.55, NAVY); pdf.text(COMPANY.name, x, y + h * 0.7);
  };
  const drawHeader = (pageNo) => {
    if (drawn.has(pageNo)) return;
    drawn.add(pageNo);
    fill(ORANGE); pdf.rect(0, 0, pageW, 1.1, "F");
    if (pageNo === 1) {
      drawLogo(M, 10, 13);
      T(F.body, "normal", 6.9, SLATE);
      pdf.text(fit(COMPANY.address, 118), M, 28.6);
      pdf.text(fit(`${COMPANY.email}   ·   V.D. ${COMPANY.taxOffice}   ·   TCKN ${COMPANY.tckn}`, 118), M, 32.4);
      T(F.body, "bold", 6.9, NAVY); pdf.text(`Tel: ${COMPANY.phone}`, M, 36.2);
      T(F.head, "bold", 17, NAVY);
      const titleStr = "GÜN SONU RAPORU", titleW = pdf.getTextWidth(titleStr);
      pdf.text(titleStr, pageW - M, 17.4, { align: "right" });
      fill(ORANGE); pdf.rect(pageW - M - titleW, 20.4, titleW, 1, "F");
      let my = 26;
      [["Rapor Günü", dateLabel, F.mono, 8.4], ["Fiş Adedi", `${NUM(t.count)} fiş · ${NUM(t.qty)} adet`, F.body, 7.6], ["Oluşturma", fmtDateTime(new Date().toISOString()), F.body, 7.2]].forEach(([l, v, font, fs]) => {
        T(font, "bold", fs, INK); const vw = pdf.getTextWidth(String(v));
        pdf.text(String(v), pageW - M, my, { align: "right" });
        T(F.body, "normal", 6.9, SLATE); pdf.text(l, pageW - M - vw - 2.5, my, { align: "right" });
        my += 4.7;
      });
      stroke(NAVY, 0.45); pdf.line(M, HEAD1 - 3, pageW - M, HEAD1 - 3);
    } else {
      drawLogo(M, 7, 9.5);
      T(F.head, "bold", 11, NAVY); pdf.text("GÜN SONU RAPORU", pageW - M, 12, { align: "right", charSpace: 0.8 });
      T(F.mono, "bold", 7, SLATE); pdf.text(`${dateLabel}  ·  devam`, pageW - M, 16.6, { align: "right" });
      stroke(LINE, 0.3); pdf.line(M, HEAD2 - 3, pageW - M, HEAD2 - 3);
    }
  };

  let y = HEAD1 + 2;
  const bottom = () => pageH - FOOT - 4;
  const ensure = (h) => { if (y + h > bottom()) { pdf.addPage(); drawHeader(pdf.getNumberOfPages()); y = HEAD2 + 2; } };
  const section = (label) => {
    ensure(12);
    T(F.body, "bold", 6.8, ORANGE); pdf.text(label, M, y + 3.2, { charSpace: 0.6 });
    const tw = pdf.getTextWidth(label) + 2;
    stroke(LINE, 0.35); pdf.line(M + tw + 2, y + 2.2, M + W, y + 2.2);
    y += 7;
  };
  // Kutu ızgarası: n sütun, her kutu etiket + değer
  const drawCells = (cells, n, h = 15) => {
    const gap = 2.5, cw = (W - gap * (n - 1)) / n;
    for (let i = 0; i < cells.length; i += n) {
      ensure(h + 3);
      cells.slice(i, i + n).forEach(([l, v, color, strong, sub], j) => {
        const x = M + j * (cw + gap);
        fill(strong ? WASH : WHITE); stroke(strong ? NAVY : LINE, strong ? 0.5 : 0.35); pdf.roundedRect(x, y, cw, h, 1.8, 1.8, "FD");
        if (strong) { fill(color || ORANGE); pdf.roundedRect(x + 1.6, y + 3.5, 1.1, h - 7, 0.5, 0.5, "F"); }
        T(F.body, "bold", 5.8, MUTED); pdf.text(fit(l, cw - 8), x + (strong ? 5.5 : 4), y + 5, { charSpace: 0.4 });
        let fs = strong ? 11.5 : 9.6; T(F.head, "bold", fs, color || INK);
        while (pdf.getTextWidth(String(v)) > cw - 8 && fs > 6.5) { fs -= 0.5; T(F.head, "bold", fs, color || INK); }
        pdf.text(String(v), x + (strong ? 5.5 : 4), y + (sub ? 10.2 : 11.2));
        if (sub) { T(F.body, "normal", 5.8, SLATE); pdf.text(fit(sub, cw - 8), x + (strong ? 5.5 : 4), y + h - 2.2); }
      });
      y += h + gap;
    }
  };
  const tableBase = {
    margin: { left: M, right: M, top: HEAD2 + 2, bottom: FOOT + 8 }, tableWidth: W, rowPageBreak: "avoid",
    styles: { font: F.body, fontSize: 7.6, cellPadding: { top: 1.7, bottom: 1.7, left: 2, right: 2 }, textColor: INK, lineColor: LINE, lineWidth: { bottom: 0.2 }, overflow: "linebreak", valign: "middle", minCellHeight: 6.5 },
    headStyles: { font: F.body, fontStyle: "bold", fillColor: HEAD_BG, textColor: NAVY, fontSize: 6.4, valign: "middle", minCellHeight: 6.5, lineWidth: 0 },
    alternateRowStyles: { fillColor: WASH },
    didDrawPage: (d) => drawHeader(d.pageNumber),
  };
  const runTable = (cfg) => { autoTable(pdf, { ...tableBase, startY: y, ...cfg }); y = pdf.lastAutoTable.finalY + 4; };
  const up = (s) => String(s || "").toLocaleUpperCase("tr-TR");

  // ---------- 1) GÜNÜN ÖZETİ ----------
  drawHeader(1);
  section("GÜNÜN ÖZETİ");
  const sumCells = [["TOPLAM CİRO (KDV DAHİL)", money(t.gross), NAVY, true], ["NET SATIŞ (KDV HARİÇ)", money(t.net)], ["KDV", money(t.kdv)], ["FİŞ / ADET", `${NUM(t.count)} fiş · ${NUM(t.qty)} adet`]];
  if (showProfit) sumCells.push(["TOPLAM MALİYET", money(t.cost)], ["NET KÂR", money(t.profit), GREEN, true], ["KÂR MARJI", `%${NUM(t.margin)}`, GREEN], ["İADE", t.returns > 0 ? `− ${money(t.returns)}` : "—", t.returns > 0 ? RED : SLATE]);
  else if (t.returns > 0) sumCells.push(["İADE", `− ${money(t.returns)}`, RED]);
  drawCells(sumCells, 4);

  // ---------- 2) ÖDEME DAĞILIMI & NAKİT KASA ----------
  section("ÖDEME DAĞILIMI & NAKİT KASA");
  const payHead = ["ÖDEME TÜRÜ", "FİŞ", "TUTAR (KDV DAHİL)", "NET (KDV HARİÇ)"]; if (showProfit) payHead.push("KÂR");
  const payBody = ["nakit", "kredi_karti", "havale", "veresiye"].filter((k) => methods[k]).map((k) => {
    const m = methods[k]; const row = [PAY_LABEL[k] || k, NUM(m.count), money(m.gross), money(m.net)]; if (showProfit) row.push(money(m.profit)); return row;
  });
  const payTot = ["TOPLAM", NUM(t.count), money(t.gross), money(t.net)]; if (showProfit) payTot.push(money(t.profit));
  const payCol = { 0: { cellWidth: 44, fontStyle: "bold" }, 1: { cellWidth: 16, halign: "center" }, 2: { halign: "right", fontStyle: "bold" }, 3: { halign: "right", textColor: SLATE } };
  if (showProfit) payCol[4] = { halign: "right", textColor: GREEN, fontStyle: "bold" };
  runTable({ head: [payHead], body: payBody, foot: [payTot], columnStyles: payCol,
    footStyles: { font: F.body, fontStyle: "bold", fillColor: HEAD_BG, textColor: NAVY, fontSize: 7.6, lineWidth: 0 },
    didParseCell: (d) => { if (d.section === "foot") d.cell.styles.halign = d.column.index === 0 ? "left" : d.column.index === 1 ? "center" : "right"; } });
  const nak = methods.nakit || { gross: 0, count: 0 };
  drawCells([
    ["NAKİT SATIŞ (KASAYA GİREN)", money(nak.gross), GREEN, true, `${NUM(nak.count)} nakit fiş`],
    ["ALINAN NAKİT (MÜŞTERİDEN)", money(t.cash_received), INK, false, "ödeme ekranında girilen"],
    ["VERİLEN PARA ÜSTÜ", money(t.change_given), ORANGE, false, "müşteriye iade edilen"],
    ["VERESİYE (AÇIK)", money((methods.veresiye || {}).gross), AMBER, false, "ödeme alınmadı · cariye yazıldı"],
  ], 4, 16);

  // ---------- 3) PERSONEL BAZINDA ----------
  if ((z.by_user || []).length) {
    section("PERSONEL BAZINDA");
    const uh = ["PERSONEL", "ROL", "FİŞ", "CİRO (KDV DAHİL)", "NET"]; if (showProfit) uh.push("KÂR");
    const ub = z.by_user.map((u) => { const r = [u.name, u.role === "admin" ? "Yönetici" : u.role === "worker" ? "Depo Müdürü" : "—", NUM(u.count), money(u.gross), money(u.net)]; if (showProfit) r.push(money(u.profit)); return r; });
    const uc = { 0: { fontStyle: "bold" }, 1: { cellWidth: 26, textColor: SLATE }, 2: { cellWidth: 14, halign: "center" }, 3: { cellWidth: 34, halign: "right", fontStyle: "bold" }, 4: { cellWidth: 30, halign: "right", textColor: SLATE } };
    if (showProfit) uc[5] = { cellWidth: 28, halign: "right", textColor: GREEN, fontStyle: "bold" };
    runTable({ head: [uh], body: ub, columnStyles: uc });
  }

  // ---------- 4) SEPET BAZLI AYRINTI ----------
  section(`SEPET BAZLI AYRINTI · ${NUM(sales.length)} FİŞ`);
  if (!sales.length) { T(F.body, "normal", 8, SLATE); pdf.text("Bu gün için satış yok.", M, y + 4); y += 10; }
  sales.forEach((s, i) => {
    const items = s.items || [];
    const bandH = 12.5;
    ensure(bandH + (showItems ? 14 : 4));
    // Fiş bandı: # · belge no · saat · müşteri · ödeme · mod · personel  |  tutar · kâr
    fill(HEAD_BG); stroke(LINE, 0.3); pdf.roundedRect(M, y, W, bandH, 1.6, 1.6, "FD");
    fill(ORANGE); pdf.rect(M, y + 2, 1, bandH - 4, "F");
    T(F.mono, "bold", 8, NAVY); const noStr = `#${i + 1}  ${receiptBelgeNo(s)}`; pdf.text(noStr, M + 4, y + 4.8);
    const noW = pdf.getTextWidth(noStr) + 7;
    T(F.body, "bold", 7.4, INK);
    pdf.text(fit(`${timeOf(s.created_at)}  ·  ${s.title || "Perakende Müşteri"}`, W - noW - 62), M + noW, y + 4.8);
    const pay = s.payment_method === "karma" ? `Karma (${(s.payments || []).map((p) => `${PAY_LABEL[p.method] || p.method} ${money(p.amount)}`).join(", ")})` : (PAY_LABEL[s.payment_method] || "Nakit");
    const others = (s.participants || []).filter((p) => p.name && p.name !== (s.completed_by || s.user_name)).map((p) => `${p.name}${roleShort(p.role) ? ` (${roleShort(p.role)})` : ""}`);
    const staff = `Personel: ${s.completed_by || s.user_name || "-"}${others.length ? ` · Sepette: ${others.join(", ")}` : ""}`;
    T(F.body, "normal", 6.6, SLATE);
    pdf.text(fit(`${pay}  ·  ${MODE_LABEL[s.mode] || "Genel Satış"}  ·  ${NUM(s.item_count || items.length)} kalem  ·  ${staff}`, W - 62), M + 4, y + 9.6);
    T(F.head, "bold", 10.5, NAVY); pdf.text(money(s.gross), M + W - 3, y + 5.2, { align: "right" });
    if (showProfit) { T(F.body, "bold", 6.6, s.profit >= 0 ? GREEN : RED); pdf.text(`Kâr ${money(s.profit)}${s.net > 0 ? ` · %${NUM(r2((s.profit / s.net) * 100))}` : ""}`, M + W - 3, y + 9.6, { align: "right" }); }
    else if (s.returned > 0) { T(F.body, "bold", 6.6, RED); pdf.text(`İade − ${money(s.returned)}`, M + W - 3, y + 9.6, { align: "right" }); }
    y += bandH + 1.5;

    if (showItems && items.length) {
      const ih = ["#", "ÜRÜN", "MİKTAR", "BİRİM ₺"]; if (showProfit) ih.push("MALİYET/BR"); ih.push("TUTAR"); if (showProfit) ih.push("KÂR");
      const ib = items.map((it, k) => {
        const q = Number(it.quantity) || 0, ret = Number(it.returned) || 0;
        const ug = it.unit_gross != null ? Number(it.unit_gross) : r2((Number(it.unit_net) || 0) * (1 + (Number(it.kdv_rate) || 0) / 100));
        const ln = it.line_net != null ? Number(it.line_net) : r2((Number(it.unit_net) || 0) * q);
        const lc = it.line_cost != null ? Number(it.line_cost) : r2((Number(it.unit_cost) || 0) * q);
        const lt = it.line_total != null ? Number(it.line_total) : r2(ug * q);
        const name = [String(it.name || "").trim(), [it.code ? `Kod: ${it.code}` : "", it.brand || "", it.drum_label ? `Makara: ${it.drum_label}` : ""].filter(Boolean).join("  ·  ")].filter(Boolean).join("\n");
        const row = [String(k + 1), name, `${NUM(q)} ${unitLabel(it.unit, "ADET")}${ret > 0 ? `\n(iade ${NUM(ret)})` : ""}`, money(ug)];
        if (showProfit) row.push(money(it.unit_cost));
        row.push(money(lt));
        if (showProfit) row.push(money(r2(ln - lc)));
        return row;
      });
      const ic = { 0: { cellWidth: 7, halign: "center", textColor: MUTED }, 1: { fontStyle: "bold" }, 2: { cellWidth: 22, halign: "center" }, 3: { cellWidth: 24, halign: "right" } };
      let ci = 4;
      if (showProfit) ic[ci++] = { cellWidth: 24, halign: "right", textColor: SLATE };
      ic[ci++] = { cellWidth: 26, halign: "right", fontStyle: "bold", textColor: NAVY };
      if (showProfit) ic[ci++] = { cellWidth: 24, halign: "right", fontStyle: "bold", textColor: GREEN };
      runTable({ head: [ih], body: ib, columnStyles: ic, styles: { ...tableBase.styles, fontSize: 7.2, minCellHeight: 6 },
        willDrawCell: (d) => { if (d.section === "body" && d.column.index === 1 && typeof d.cell.raw === "string") { const parts = d.cell.raw.split("\n"); d.cell._name = parts[0]; d.cell._subs = parts.slice(1); d.cell.text = []; } },
        didParseCell: (d) => { if (d.section === "body" && d.column.index === ih.length - 1 && showProfit && String(d.cell.raw).startsWith("-")) d.cell.styles.textColor = RED; },
        didDrawCell: (d) => {
          if (d.cell._name == null) return;
          const { x, y: cy, width, height } = d.cell; const padL = d.cell.padding("left"), avail = width - padL - d.cell.padding("right");
          const base = 7.2, subFs = 5.9;
          T(F.body, "bold", base, INK); const nameLines = pdf.splitTextToSize(d.cell._name, avail);
          T(F.body, "normal", subFs, SLATE); const subLines = d.cell._subs.flatMap((sx) => pdf.splitTextToSize(sx, avail));
          const lh = base * 0.42, slh = subFs * 0.42;
          let ty = cy + (height - (nameLines.length * lh + subLines.length * slh)) / 2 + lh * 0.78;
          T(F.body, "bold", base, INK); nameLines.forEach((l) => { pdf.text(l, x + padL, ty); ty += lh; });
          T(F.body, "normal", subFs, SLATE); subLines.forEach((l) => { pdf.text(l, x + padL, ty); ty += slh; });
        } });
      y -= 1.5;
    }
    // Fiş dipnotları: nakit / para üstü · pazarlık · iade · not
    const notes = [];
    if (Number(s.cash_received) > 0.005) notes.push([`Alınan nakit ${money(s.cash_received)}  ·  Para üstü ${money(s.change_given)}`, SLATE]);
    if (s.override_applied && Number(s.manual_discount)) notes.push([`Pazarlık / yuvarlama: hesaplanan ${money(s.computed_total)} → ${money(Number(s.computed_total) - Number(s.manual_discount))} (${Number(s.manual_discount) > 0 ? "−" : "+"}${money(Math.abs(s.manual_discount))})`, ORANGE]);
    if (Number(s.returned) > 0) notes.push([`İade edilen ${money(s.returned)} düşülmüştür`, RED]);
    if (Number(s.veresiye_amount) > 0) notes.push([`Veresiye ${money(s.veresiye_amount)}${Number(s.collected_total) > 0 ? ` · tahsil edilen ${money(s.collected_total)}` : " · henüz tahsil edilmedi"}`, AMBER]);
    if (String(s.note || "").trim()) notes.push([`Not: ${String(s.note).trim()}`, INK]);
    if (notes.length) {
      ensure(notes.length * 3.8 + 2);
      notes.forEach(([txt, color]) => { T(F.body, color === INK ? "normal" : "bold", 6.6, color); pdf.text(fit(txt, W - 6), M + 4, y + 2.6); y += 3.8; });
    }
    y += 4;
  });

  // ---------- Alt bilgi ----------
  const n = pdf.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    pdf.setPage(i);
    const fy = pageH - FOOT + 1;
    stroke(LINE, 0.3); pdf.line(M, fy, pageW - M, fy);
    fill(ORANGE); pdf.rect(M, fy - 0.45, 12, 0.9, "F");
    T(F.body, "bold", 7, NAVY); pdf.text(COMPANY.name, M, fy + 4.8);
    T(F.body, "normal", 6.2, SLATE); pdf.text(`Gün Sonu Raporu · ${dateLabel}${showProfit ? " · maliyet/kâr içerir — yalnız yönetici" : ""}`, M, fy + 8.4);
    pdf.text(`Sayfa ${i} / ${n}`, pageW / 2, fy + 8.4, { align: "center" });
    T(F.body, "bold", 6.6, NAVY); pdf.text(COMPANY.email, pageW - M, fy + 4.8, { align: "right" });
    T(F.body, "normal", 6.2, SLATE); pdf.text(`Tel: ${COMPANY.phone}`, pageW - M, fy + 8.4, { align: "right" });
  }
  return pdf;
};
